import React, { useLayoutEffect, useState } from 'react';
import { ArrowLeft, QrCode, User, Linkedin, MessageSquare, ExternalLink } from 'lucide-react';

export const ThinkTechEventPage = () => {
  const [studentId, setStudentId] = useState('');
  const [showScore, setShowScore] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  const handleLogoClick = () => {
    window.location.href = '/';
  };

  const handleViewScore = () => {
    if (studentId.trim()) {
      setShowScore(true);
    }
  };

  const partnerColleges = [
    { name: "M.S. Engineering College", logo: "M" },
    { name: "Reva University", logo: "R" },
    { name: "Annamacharya University", logo: "A" },
    { name: "HKBK Group of Institutions", logo: "H" },
    { name: "RV University", logo: "RV" },
    { name: "Malla Reddy College of Engineering", logo: "MR" }
  ];

  return (
    <main className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b shadow-lg" style={{borderColor: '#6B4423'}}>
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-2 sm:py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back button and A2ZCampus Logo */}
            <div className="flex items-center">
              <button
                onClick={handleBackToHome}
                className="mr-2 sm:mr-3 p-1 sm:p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Back to home"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" style={{color: '#6B4423'}} />
              </button>
              <button 
                onClick={handleLogoClick}
                className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
                aria-label="Go to home page"
              >
                <img 
                  src="/A2ZAssist%20logo.png" 
                  alt="A2ZCampus Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 mr-1 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <div className="flex items-center">
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl tracking-tight" style={{color: '#2C2420'}}>A2Z</span>
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl tracking-tight" style={{color: '#6B4423'}}>Campus</span>
                  <span className="text-xs sm:text-sm md:text-sm lg:text-base font-bold ml-1 align-top" style={{color: '#6B4423'}}>™</span>
                </div>
              </button>
            </div>
            
            {/* Right: Event Title */}
            <div className="hidden lg:flex items-center bg-white border-2 rounded-2xl px-4 py-2 shadow-lg" style={{borderColor: '#C4A052'}}>
              <QrCode className="w-5 h-5 mr-2" style={{color: '#C4A052'}} />
              <div className="flex items-center">
                <span className="font-bold text-base" style={{color: '#6B4423'}}>ThinkTech™: Clarity Before Careers</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-4 sm:py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            {/* Mobile Event Title */}
            <div className="lg:hidden flex justify-center mb-2 sm:mb-3 md:mb-4">
              <div className="flex items-center bg-white border-2 rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-2 shadow-lg" style={{borderColor: '#C4A052'}}>
                <QrCode className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 mr-2 md:mr-3" style={{color: '#C4A052'}} />
                <div className="flex flex-col items-center">
                  <span className="font-bold text-xs sm:text-sm md:text-base text-center" style={{color: '#6B4423'}}>ThinkTech™: Clarity Before Careers</span>
                </div>
              </div>
            </div>

            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 px-2" style={{color: '#2C2420'}}>
              ThinkTech™: <span style={{color: '#C4A052'}}>Clarity Before Careers</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-4xl mx-auto leading-relaxed mb-3 sm:mb-4 md:mb-6 font-medium px-3" style={{color: '#6B4423'}}>
              Your journey to clarity starts here
            </p>
          </div>
        </div>
      </section>

      {/* Section 1 - Clarity Score QR Code */}
      <section className="py-6 md:py-8" style={{backgroundColor: '#FFFFFF'}}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-4 md:mb-6" style={{borderColor: '#C4A052', color: '#6B4423'}}>
              <QrCode className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" style={{color: '#C4A052'}} />
              Clarity Score Assessment
            </div>
            
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl border-2 max-w-md mx-auto" style={{borderColor: '#C4A052'}}>
              <div className="w-48 h-48 md:w-64 md:h-64 mx-auto mb-4 md:mb-6 bg-gray-100 rounded-2xl flex items-center justify-center border-2" style={{borderColor: '#6B4423'}}>
                <a
                  href="https://forms.gle/uUqbBUsoWVzYiQNu8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full hover:opacity-90 transition-opacity"
                >
                  <img 
                    src="/ThinkTech%20ClarityScore%20(1).png" 
                    alt="QR Code for Clarity Score Assessment" 
                    className="w-full h-full object-contain rounded-xl"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="text-center flex flex-col items-center justify-center h-full">
                            <div class="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3" style="color: #C4A052">
                              <svg fill="currentColor" viewBox="0 0 24 24"><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4z"/><path d="M15 13h2v2h-2zM17 15h2v2h-2zM19 13h2v2h-2zM15 17h2v2h-2zM17 19h2v2h-2zM19 17h2v2h-2zM13 15h2v2h-2zM13 19h2v2h-2z"/></svg>
                            </div>
                            <p class="text-sm md:text-base font-medium" style="color: #6B4423">
                              QR Code for<br />Clarity Score Form
                            </p>
                          </div>
                        `;
                      }
                    }}
                  />
                </a>
              </div>
              <p className="text-lg md:text-xl font-bold" style={{color: '#2C2420'}}>
                Scan to see your Clarity Score
              </p>
              <p className="text-sm md:text-base mt-2" style={{color: '#6B4423'}}>
                Discover where you stand on your career clarity journey
              </p>
              <a
                href="https://forms.gle/uUqbBUsoWVzYiQNu8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 px-6 py-3 text-white font-bold rounded-xl hover:opacity-90 transition-all transform hover:scale-105"
                style={{backgroundColor: '#C4A052'}}
              >
                Take Clarity Assessment
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 - Your Clarity Score */}
      <section className="py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-4 md:mb-6" style={{borderColor: '#C4A052', color: '#6B4423'}}>
                <User className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" style={{color: '#C4A052'}} />
                Your Clarity Score
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl border border-gray-200">
              {!showScore ? (
                <div className="text-center">
                  <h3 className="text-xl md:text-2xl font-bold mb-4" style={{color: '#2C2420'}}>
                    Enter Your Student ID
                  </h3>
                  <p className="text-sm md:text-base mb-6" style={{color: '#6B4423'}}>
                    Enter your unique student ID to view your personalized clarity score
                  </p>
                  <div className="max-w-md mx-auto">
                    <input
                      type="text"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      placeholder="Enter your Student ID"
                      className="w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:border-transparent transition-all text-center font-medium"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    />
                    <button
                      onClick={handleViewScore}
                      disabled={!studentId.trim()}
                      className={`w-full mt-4 px-6 py-3 text-white font-bold rounded-xl transition-all transform hover:scale-105 ${
                        !studentId.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                      }`}
                      style={{backgroundColor: '#C4A052'}}
                    >
                      View My Clarity Score
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-6 rounded-full flex items-center justify-center text-white text-4xl md:text-5xl font-bold" style={{backgroundColor: '#C4A052'}}>
                    85
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{color: '#2C2420'}}>
                    Your Clarity Score: 85/100
                  </h3>
                  <p className="text-lg md:text-xl font-semibold mb-4" style={{color: '#6B4423'}}>
                    Great Progress! You're on the right track.
                  </p>
                  <p className="text-sm md:text-base max-w-2xl mx-auto" style={{color: '#2C2420'}}>
                    Student ID: {studentId} - You have a strong foundation in career clarity. 
                    Continue building on your strengths and focus on areas for improvement.
                  </p>
                  <button
                    onClick={() => setShowScore(false)}
                    className="mt-4 px-6 py-2 border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                    style={{borderColor: '#6B4423', color: '#6B4423'}}
                  >
                    Check Another ID
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - Connect with Us */}
      <section className="py-6 md:py-8" style={{backgroundColor: '#FFFFFF'}}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-4 md:mb-6" style={{borderColor: '#C4A052', color: '#6B4423'}}>
                <img 
                  src="/A2ZAssist%20logo.png" 
                  alt="A2Z Assist Logo" 
                  className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
                />
                Connect with Us
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-4" style={{color: '#2C2420'}}>
                Stay Connected with A2ZCampus™
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Personal LinkedIn */}
              <a
                href="https://www.linkedin.com/in/veeranath-srikar/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-200 group"
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{backgroundColor: '#0077B5'}}>
                    <Linkedin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{color: '#2C2420'}}>
                    Veeranath Srikar
                  </h3>
                  <p className="text-sm mb-3" style={{color: '#6B4423'}}>
                    Founder & Personal Account
                  </p>
                  <div className="flex items-center justify-center text-sm group-hover:opacity-80">
                    <span style={{color: '#0077B5'}}>Connect on LinkedIn</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </a>

              {/* A2ZCampus LinkedIn */}
              <a
                href="https://www.linkedin.com/showcase/a2zcampus/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-200 group"
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{backgroundColor: '#0077B5'}}>
                    <Linkedin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{color: '#2C2420'}}>
                    A2ZCampus™
                  </h3>
                  <p className="text-sm mb-3" style={{color: '#6B4423'}}>
                    Company Page
                  </p>
                  <div className="flex items-center justify-center text-sm group-hover:opacity-80">
                    <span style={{color: '#0077B5'}}>Follow Us</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </a>

              {/* WhatsApp */}
              <a
                href="https://chat.whatsapp.com/K92aNPM8garFqT71gZcEOp"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-200 group"
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{backgroundColor: '#25D366'}}>
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{color: '#2C2420'}}>
                    WhatsApp Community
                  </h3>
                  <p className="text-sm mb-3" style={{color: '#6B4423'}}>
                    Join the Discussion
                  </p>
                  <div className="flex items-center justify-center text-sm group-hover:opacity-80">
                    <span style={{color: '#25D366'}}>Join Chat</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Partner Colleges */}
      <section className="py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-4 md:mb-6" style={{borderColor: '#C4A052', color: '#6B4423'}}>
                <img 
                  src="/A2ZAssist%20logo.png" 
                  alt="A2Z Assist Logo" 
                  className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
                />
                Partner Colleges
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-4" style={{color: '#2C2420'}}>
                Trusted by Leading Institutions
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {partnerColleges.map((college, index) => (
                <div key={index} className="bg-white rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200 group">
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#6B4423'}}>
                      <span className="text-white font-bold text-sm md:text-lg">{college.logo}</span>
                    </div>
                    <h3 className="text-xs md:text-sm font-bold leading-tight group-hover:opacity-80 transition-colors" style={{color: '#2C2420'}}>
                      {college.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 md:py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <button 
              onClick={handleLogoClick}
              className="flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer mb-4 mx-auto"
              aria-label="Go to home page"
            >
              <img 
                src="/A2ZAssist%20logo.png" 
                alt="A2ZCampus Logo" 
                className="w-10 h-10 md:w-12 md:h-12 mr-3 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <div className="flex items-center">
                <span className="font-bold text-xl md:text-2xl tracking-tight" style={{color: '#2C2420'}}>A2Z</span>
                <span className="font-bold text-xl md:text-2xl tracking-tight" style={{color: '#6B4423'}}>Campus</span>
                <span className="text-sm md:text-base font-bold ml-1 align-top" style={{color: '#6B4423'}}>™</span>
              </div>
            </button>
            
            <p className="text-sm md:text-base mb-2" style={{color: '#6B4423'}}>
              © 2025 A2ZCampus™. All rights reserved.
            </p>
            
            <p className="text-xs md:text-sm font-medium" style={{color: '#C4A052'}}>
              Made for ThinkTech™
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
};